import { useMemo, useState } from 'react'
import { EVENT_COLORS } from '../../config'
import { useSync } from '../../context/SyncContext'
import { useEvents } from '../../hooks/useEvents'
import type { CalendarEvent, CalendarView, EventFormData } from '../../types'
import {
  addDays,
  addMonths,
  addYears,
  defaultEventRange,
  formatMonthYear,
  startOfWeek,
  toLocalInputValue,
} from '../../utils/dateUtils'
import { DayView } from './DayView'
import { EventModal } from './EventModal'
import { MonthView } from './MonthView'
import { WeekView } from './WeekView'
import { YearView } from './YearView'

const VIEWS: { id: CalendarView; label: string }[] = [
  { id: 'year', label: 'Jahr' },
  { id: 'month', label: 'Monat' },
  { id: 'week', label: 'Woche' },
  { id: 'day', label: 'Tag' },
]

export function Calendar() {
  const { addEvent, updateEvent, deleteEvent, eventsOnDay, hasEventsOnDay } =
    useEvents()
  const { settings, setCalendarView } = useSync()
  const [cursor, setCursor] = useState(() => new Date())
  const view = settings.calendarView
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<CalendarEvent | null>(null)
  const [formInitial, setFormInitial] = useState<EventFormData>(() => {
    const range = defaultEventRange(new Date())
    return {
      title: '',
      start: range.start,
      end: range.end,
      notes: '',
      color: EVENT_COLORS[0],
    }
  })

  const title = useMemo(() => {
    if (view === 'year') return String(cursor.getFullYear())
    if (view === 'month') return formatMonthYear(cursor)
    if (view === 'week') {
      const start = startOfWeek(cursor)
      const end = addDays(start, 6)
      return `${start.getDate()}.–${end.getDate()}. ${formatMonthYear(end)}`
    }
    return formatMonthYear(cursor)
  }, [cursor, view])

  const goPrev = () => {
    if (view === 'year') setCursor((c) => addYears(c, -1))
    else if (view === 'month') setCursor((c) => addMonths(c, -1))
    else if (view === 'week') setCursor((c) => addDays(c, -7))
    else setCursor((c) => addDays(c, -1))
  }

  const goNext = () => {
    if (view === 'year') setCursor((c) => addYears(c, 1))
    else if (view === 'month') setCursor((c) => addMonths(c, 1))
    else if (view === 'week') setCursor((c) => addDays(c, 7))
    else setCursor((c) => addDays(c, 1))
  }

  const goToday = () => setCursor(new Date())

  const openCreate = (day?: Date) => {
    const base = day ?? cursor
    const range = defaultEventRange(base)
    setEditing(null)
    setFormInitial({
      title: '',
      start: range.start,
      end: range.end,
      notes: '',
      color: EVENT_COLORS[0],
    })
    setModalOpen(true)
  }

  const openEdit = (event: CalendarEvent) => {
    setEditing(event)
    setFormInitial({
      title: event.title,
      start: event.start.length === 16 ? event.start : toLocalInputValue(new Date(event.start)),
      end: event.end.length === 16 ? event.end : toLocalInputValue(new Date(event.end)),
      notes: event.notes ?? '',
      color: event.color,
    })
    setModalOpen(true)
  }

  const handleSave = (data: EventFormData) => {
    if (editing) updateEvent(editing.id, data)
    else addEvent(data)
    setModalOpen(false)
  }

  const handleDelete = () => {
    if (editing) {
      deleteEvent(editing.id)
      setModalOpen(false)
    }
  }

  /** Year → Month */
  const selectMonth = (d: Date) => {
    setCursor(d)
    setCalendarView('month')
  }

  /** Month → Week */
  const selectWeek = (d: Date) => {
    setCursor(startOfWeek(d))
    setCalendarView('week')
  }

  /** Month/Week → Day */
  const selectDay = (d: Date) => {
    setCursor(d)
    setCalendarView('day')
  }

  return (
    <div className="calendar">
      <header className="calendar__toolbar">
        <div className="calendar__nav">
          <button type="button" className="neon-btn neon-btn--ghost" onClick={goPrev} aria-label="Zurück">
            ◀
          </button>
          <button type="button" className="neon-btn neon-btn--ghost" onClick={goToday}>
            Heute
          </button>
          <button type="button" className="neon-btn neon-btn--ghost" onClick={goNext} aria-label="Weiter">
            ▶
          </button>
        </div>

        <h2 className="calendar__title">{title}</h2>

        <div className="calendar__views" role="tablist" aria-label="Kalenderansicht">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={view === v.id}
              className={`view-chip${view === v.id ? ' view-chip--active' : ''}`}
              onClick={() => setCalendarView(v.id)}
            >
              {v.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="neon-btn neon-btn--primary calendar__add"
          onClick={() => openCreate()}
        >
          + Termin
        </button>
      </header>

      <div className="calendar__body">
        {view === 'year' && (
          <YearView
            year={cursor.getFullYear()}
            hasEventsOnDay={hasEventsOnDay}
            onSelectMonth={selectMonth}
          />
        )}
        {view === 'month' && (
          <MonthView
            year={cursor.getFullYear()}
            month={cursor.getMonth()}
            eventsOnDay={eventsOnDay}
            onSelectDay={selectDay}
            onSelectWeek={selectWeek}
            onSelectEvent={openEdit}
          />
        )}
        {view === 'week' && (
          <WeekView
            anchor={cursor}
            eventsOnDay={eventsOnDay}
            onSelectDay={selectDay}
            onSelectEvent={openEdit}
          />
        )}
        {view === 'day' && (
          <DayView
            day={cursor}
            events={eventsOnDay(cursor)}
            onSelectEvent={openEdit}
            onAdd={() => openCreate(cursor)}
          />
        )}
      </div>

      <EventModal
        open={modalOpen}
        initial={formInitial}
        editing={editing}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        onDelete={editing ? handleDelete : undefined}
      />
    </div>
  )
}
